// ============================================================
// permisos.js — Sistema centralizado de permisos
// Lógica basada en:
//   - usuario.puede_editar (boolean) → si puede modificar datos
//   - usuario.aliado → filtra datos por contratista
//   - usuario.rol → solo distingue "administrador" del resto
// ============================================================

const ALIADOS_CONTRATISTA = ["CONECTAR TV", "TABASCO"];

/**
 * Retorna el rol normalizado (minúsculas, sin espacios extra)
 */
export function getRol(usuario) {
  return (usuario?.rol || "").toLowerCase().trim();
}

/**
 * ¿Es Administrador? → acceso total, gestión usuarios y carga dispo
 */
export function esAdmin(usuario) {
  return getRol(usuario) === "administrador";
}

/**
 * ¿Puede editar/modificar datos?
 * → Administrador siempre puede
 * → Otros usuarios solo si puede_editar === true
 */
export function puedeEditar(usuario) {
  if (esAdmin(usuario)) return true;
  return usuario?.puede_editar === true || usuario?.puede_editar === 1;
}

/**
 * ¿Es contratista? → su aliado es uno de los aliados externos
 * Solo ve datos filtrados por su aliado
 */
export function esContratista(usuario) {
  const aliado = (usuario?.aliado || "").toUpperCase().trim();
  return ALIADOS_CONTRATISTA.includes(aliado);
}

/**
 * ¿Puede ver todas las zonas/aliados sin filtro?
 * → Admin y usuarios con aliado CLARO (o sin aliado externo)
 */
export function verTodo(usuario) {
  return !esContratista(usuario);
}

/**
 * Retorna el aliado del usuario para filtrar datos
 * Solo aplica si es contratista, de lo contrario sin restricción
 */
export function getAliadoFiltro(usuario) {
  if (esContratista(usuario)) return usuario?.aliado || "";
  return "";
}

/**
 * Funciones de compatibilidad — mantienen el código existente funcionando
 * sin necesidad de modificar cada componente que ya las importa
 */
export function esSoloLecturaClaro(usuario) {
  // Antes era para Gerente/Jefe General, Directo Claro, Owner
  // Ahora: cualquier usuario de Claro (no contratista) sin permiso de edición
  return !esContratista(usuario) && !puedeEditar(usuario);
}

export function puedeEditar_legacy(usuario) {
  return puedeEditar(usuario);
}

/**
 * ─────────────────────────────────────────────────────────────
 * PERMISOS POR MÓDULO (granular) — agregado para poder decidir,
 * usuario por usuario, EN QUÉ SECCIONES PUEDE EDITAR y en cuáles
 * solo puede ver. Es un permiso ADICIONAL y OPCIONAL por encima
 * del sistema de "puede_editar" de siempre:
 *
 *   - Si el Admin nunca toca el permiso por módulos de un usuario
 *     (usuario.modulos_edicion es null/undefined), todo sigue
 *     funcionando EXACTAMENTE igual que antes: si puede_editar es
 *     true, edita en todos los módulos; si es false, no edita en
 *     ninguno.
 *   - Si el Admin sí elige módulos específicos para ese usuario
 *     (modulos_edicion pasa a ser un array, aunque esté vacío),
 *     ese usuario SOLO puede editar en los módulos de esa lista,
 *     sin importar lo que diga puede_editar.
 *
 * Solo se listan aquí los módulos que REALMENTE tienen contenido
 * editable hoy (los demás — Home, Reporte Ofensores, Resumen ICR,
 * Causas de Falla, Semáforo Nodos — son solo consulta/reportes,
 * no tienen ninguna acción de edición que restringir).
 * ─────────────────────────────────────────────────────────────
 */
export const MODULOS_EDITABLES = [
  { key: "planes",             label: "Planes de Trabajo" },
  { key: "conciliaciones",     label: "Conciliaciones Ofensores" },
  { key: "ofensores_n2",       label: "Panel Ofensores N2+" },
  { key: "backlog_r3",         label: "Backlog OM Fijo R3" },
  { key: "backlog_r3_revision", label: "Marcar OT para revisión (Backlog R3)" },
  { key: "escalamientos_certificar", label: "Certificar nodos (Escalamientos Huella HFC)" },
];

/**
 * ¿Puede editar dentro de este módulo en particular?
 * moduloKey: una de las "key" de MODULOS_EDITABLES.
 */
export function puedeEditarModulo(usuario, moduloKey) {
  if (esAdmin(usuario)) return true;
  const modulos = usuario?.modulos_edicion;
  if (Array.isArray(modulos)) {
    // El Admin ya configuró permisos específicos para este usuario:
    // solo cuentan los módulos de esa lista, nada más.
    return modulos.includes(moduloKey);
  }
  // Nunca se configuraron permisos específicos → comportamiento de
  // siempre (todo o nada, según puede_editar).
  return puedeEditar(usuario);
}

/**
 * ¿Puede marcar/desmarcar una OT del Backlog R3 "para revisión"?
 * Este permiso es INDEPENDIENTE del permiso general de edición del
 * módulo ("backlog_r3"): a propósito NO hereda nada de puede_editar.
 * Por defecto nadie lo tiene (ni siquiera quien ya puede editar el
 * backlog) — solo el Administrador siempre, y cualquier otro usuario
 * únicamente si el Admin le marcó la casilla "backlog_r3_revision"
 * de forma explícita en Gestión de Usuarios.
 */
export function puedeMarcarRevisionBacklog(usuario) {
  if (esAdmin(usuario)) return true;
  const modulos = usuario?.modulos_edicion;
  return Array.isArray(modulos) && modulos.includes("backlog_r3_revision");
}

/**
 * ¿Puede marcar un escalamiento como "Nodo Certificado"? Permiso NUEVO e
 * INDEPENDIENTE del permiso general de edición del módulo (a propósito no
 * se apoya en puede_editar) — por defecto solo el Administrador lo tiene;
 * cualquier otro usuario necesita que el Admin le marque
 * "escalamientos_certificar" en Gestión de Usuarios. Se certifica así
 * porque ese paso normalmente lo confirma el NOC/Admin, no cualquiera que
 * reporte el caso.
 */
export function puedeCertificarEscalamiento(usuario) {
  if (esAdmin(usuario)) return true;
  const modulos = usuario?.modulos_edicion;
  return Array.isArray(modulos) && modulos.includes("escalamientos_certificar");
}

/**
 * Componente de banner para modo solo lectura.
 * Muéstralo en vistas donde el usuario no puede editar.
 */
export function BannerSoloLectura() {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: "6px",
      background: "#f0f9ff", border: "1px solid #bae6fd",
      borderRadius: "20px", padding: "4px 12px",
      fontSize: "11px", fontWeight: "600", color: "#0369a1",
      marginLeft: "8px"
    }}>
      <span>👁️</span> Modo consulta
    </div>
  );
}

/**
 * Bloque de acceso denegado para rutas restringidas
 */
export function AccesoDenegado({ mensaje }) {
  return (
    <div style={{ padding: "40px", textAlign: "center" }}>
      <div style={{
        background: "#fef2f2", border: "1px solid #fca5a5",
        borderRadius: "12px", padding: "40px",
        maxWidth: "400px", margin: "0 auto"
      }}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
        <h3 style={{ color: "#991b1b", margin: "0 0 8px 0" }}>Acceso restringido</h3>
        <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>
          {mensaje || "Esta sección solo está disponible para el Administrador."}
        </p>
      </div>
    </div>
  );
}